import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CITY, type LatLng } from '@kick/types';

/** Dirección desde la que el cliente está comprando. */
export interface UbicacionElegida {
  /** Null cuando es una ubicación del GPS y no una dirección guardada. */
  addressId: string | null;
  label: string;
  addressLine: string;
  location: LatLng;
}

interface EstadoUbicacion {
  ubicacion: UbicacionElegida | null;
  /** El navegador está resolviendo el GPS. */
  localizando: boolean;
  /** Motivo por el que no se pudo obtener la ubicación, para explicarlo. */
  error: string | null;

  fijar: (ubicacion: UbicacionElegida) => void;
  limpiar: () => void;
  usarGps: () => Promise<void>;
}

export const useUbicacion = create<EstadoUbicacion>()(
  persist(
    (set) => ({
      ubicacion: null,
      localizando: false,
      error: null,

      fijar: (ubicacion) => set({ ubicacion, error: null }),
      limpiar: () => set({ ubicacion: null }),

      usarGps: async () => {
        if (!navigator.geolocation) {
          set({ error: 'Tu navegador no permite compartir la ubicación' });
          return;
        }

        set({ localizando: true, error: null });

        try {
          const posicion = await new Promise<GeolocationPosition>((resolver, rechazar) => {
            navigator.geolocation.getCurrentPosition(resolver, rechazar, {
              enableHighAccuracy: true,
              timeout: 10_000,
              maximumAge: 60_000,
            });
          });

          set({
            ubicacion: {
              addressId: null,
              label: 'Ubicación actual',
              addressLine: 'Estás comprando desde tu ubicación actual',
              location: {
                lat: posicion.coords.latitude,
                lng: posicion.coords.longitude,
              },
            },
            localizando: false,
          });
        } catch (error) {
          const codigo = (error as GeolocationPositionError)?.code;
          const mensajes: Record<number, string> = {
            1: 'No diste permiso para usar tu ubicación. Puedes elegir una dirección a mano.',
            2: 'No pudimos determinar dónde estás. Intenta con una dirección.',
            3: 'La búsqueda de tu ubicación tardó demasiado.',
          };
          set({
            localizando: false,
            error: mensajes[codigo] ?? 'No pudimos obtener tu ubicación',
          });
        }
      },
    }),
    {
      name: 'kick-ubicacion',
      partialize: (estado) => ({ ubicacion: estado.ubicacion }),
    },
  ),
);

/**
 * Punto desde el que se buscan comercios.
 * Sin ubicación elegida se usa el centro de la ciudad: así la tienda muestra
 * algo útil desde el primer momento, sin exigir permisos ni registro.
 */
export function puntoDeBusqueda(ubicacion: UbicacionElegida | null): LatLng {
  return ubicacion?.location ?? CITY.center;
}
